import { useCallback, useEffect, useState } from "react";
import { InvestmentsSection } from "./assets/InvestmentsSection";
import { CryptoSection } from "./assets/CryptoSection";
import { AccountsSection } from "./assets/AccountsSection";
import type {
  CryptoOverview,
  DerivedBankAccount,
  InvestmentsOverview,
  ManualAccount,
} from "../types";
import type { AssetsSection } from "../App";

interface AssetsPageProps {
  section: AssetsSection;
  onSectionChange: (section: AssetsSection) => void;
}

const SECTIONS: Array<{ id: AssetsSection; label: string }> = [
  { id: "investments", label: "Investeringar" },
  { id: "crypto", label: "Krypto" },
  { id: "accounts", label: "Konton & kort" },
];

export function AssetsPage({ section, onSectionChange }: AssetsPageProps) {
  const [investments, setInvestments] = useState<InvestmentsOverview | null>(null);
  const [cryptoData, setCryptoData] = useState<CryptoOverview | null>(null);
  const [accounts, setAccounts] = useState<ManualAccount[] | null>(null);
  const [derived, setDerived] = useState<DerivedBankAccount[] | null>(null);

  const refresh = useCallback(() => {
    fetch("/api/investments")
      .then((r) => r.json())
      .then(setInvestments)
      .catch(() => {});
    fetch("/api/crypto")
      .then((r) => r.json())
      .then(setCryptoData)
      .catch(() => {});
    fetch("/api/accounts")
      .then((r) => r.json())
      .then((data) => {
        setAccounts(data.accounts ?? []);
        setDerived(data.derived ?? []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Tillgångar</h2>
        <div className="flex rounded-lg bg-muted p-1">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => onSectionChange(s.id)}
              className={`flex-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors sm:flex-none ${
                section === s.id
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {section === "investments" && (
        <InvestmentsSection data={investments} onRefresh={refresh} />
      )}
      {section === "crypto" && <CryptoSection data={cryptoData} onRefresh={refresh} />}
      {section === "accounts" && (
        <AccountsSection accounts={accounts} derived={derived} onRefresh={refresh} />
      )}
    </div>
  );
}
